/**
 * ADR-0068: Mine / Teams / Shared scope tabs. Writes `?tab=` to the URL for
 * deep-link survival (spec §2 line 113; §8 line 249 role="tablist").
 *
 * Behavior:
 *   - Default active tab = URL `?tab=` → else localStorage → else "mine".
 *   - Changing tab does a shallow navigation that only replaces `?tab=`.
 *   - When counts are supplied, each tab shows its count as a pill suffix.
 *   - Empty-group-list mode (spec §2 line 115): if `inZeroGroups` is true,
 *     the Teams tab renders as disabled + shows a tooltip hint (the list
 *     page renders the "not a team member yet" help line below the tabs).
 */

import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import type { ScopeCounts, ScopeTab } from "../lib/scope.js";

const LS_PREFIX = "atlas.scopeTabs.";

function storageKey(args: { tenantId: string; accountId: string; entity: string }): string {
  return `${LS_PREFIX}${args.tenantId}.${args.accountId}.${args.entity}`;
}

function readDefaultTab(args: {
  tenantId: string;
  accountId: string;
  entity: string;
}): ScopeTab {
  try {
    const raw = localStorage.getItem(storageKey(args));
    if (raw === "mine" || raw === "teams" || raw === "shared") return raw;
  } catch {
    /* ignore quota / unavailable */
  }
  return "mine";
}

function writeDefaultTab(
  args: { tenantId: string; accountId: string; entity: string },
  tab: ScopeTab
): void {
  try {
    localStorage.setItem(storageKey(args), tab);
  } catch {
    /* ignore */
  }
}

export function ScopeTabs(props: {
  /** Current URL tab, if any. Parent reads this from the URL and passes through. */
  currentTab: ScopeTab | null;
  counts?: ScopeCounts | null;
  /** Base path for the tab links. Each tab appends `?tab=<name>`. */
  basePath: string;
  /** Extra query params to preserve across tab switches (e.g., search `q`). */
  extraSearch?: URLSearchParams;
  /** When true, the Teams tab is rendered disabled with a tooltip. */
  inZeroGroups?: boolean;
  /** Storage-key fingerprint: (tenant, user, entity). LocalStorage fallback for empty URL. */
  storageFingerprint: { tenantId: string; accountId: string; entity: string };
}) {
  const resolved: ScopeTab = props.currentTab ?? readDefaultTab(props.storageFingerprint);

  // Persist the resolved tab as the new localStorage default on mount + on change.
  useEffect(() => {
    writeDefaultTab(props.storageFingerprint, resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, props.storageFingerprint.tenantId, props.storageFingerprint.accountId, props.storageFingerprint.entity]);

  const mkHref = (tab: ScopeTab): string => {
    const qs = new URLSearchParams(props.extraSearch ?? "");
    qs.set("tab", tab);
    return `${props.basePath}?${qs.toString()}`;
  };

  return (
    <div className="scopeTabs" role="tablist" aria-label="Scope">
      <Link
        to={mkHref("mine")}
        role="tab"
        aria-selected={resolved === "mine"}
        className={`scopeTab${resolved === "mine" ? " isActive" : ""}`}
      >
        Personal
        {props.counts ? <span className="scopeTabCount">{props.counts.mine}</span> : null}
      </Link>
      {props.inZeroGroups ? (
        <span
          role="tab"
          aria-selected={false}
          aria-disabled="true"
          className="scopeTab isDisabled"
          title="You are not a member of any team yet."
        >
          Teams
          {props.counts ? <span className="scopeTabCount">{props.counts.teams}</span> : null}
        </span>
      ) : (
        <Link
          to={mkHref("teams")}
          role="tab"
          aria-selected={resolved === "teams"}
          className={`scopeTab${resolved === "teams" ? " isActive" : ""}`}
        >
          Teams
          {props.counts ? <span className="scopeTabCount">{props.counts.teams}</span> : null}
        </Link>
      )}
      <Link
        to={mkHref("shared")}
        role="tab"
        aria-selected={resolved === "shared"}
        className={`scopeTab${resolved === "shared" ? " isActive" : ""}`}
      >
        Shared
        {props.counts ? <span className="scopeTabCount">{props.counts.shared}</span> : null}
      </Link>
    </div>
  );
}

/**
 * Helper: parse `?tab=` from the current URL and coerce to ScopeTab | null.
 * Routes use this to feed `<ScopeTabs currentTab={...} />`.
 */
export function useScopeTabFromUrl(): ScopeTab | null {
  const [sp] = useSearchParams();
  const raw = sp.get("tab");
  if (raw === "mine" || raw === "teams" || raw === "shared") return raw;
  return null;
}

/**
 * Resolved scope tab — URL `?tab=` → localStorage → "mine". Matches what
 * `<ScopeTabs>` shows as selected, so the route's data fetch and the tab UI
 * can never disagree.
 */
export function useResolvedScopeTab(fingerprint: {
  tenantId: string;
  accountId: string;
  entity: string;
}): ScopeTab {
  const fromUrl = useScopeTabFromUrl();
  return fromUrl ?? readDefaultTab(fingerprint);
}

/**
 * Helper: current location's pathname — used when a route wants to
 * programmatically compose the basePath for <ScopeTabs/>.
 */
export function useScopeTabsBasePath(): string {
  const loc = useLocation();
  return loc.pathname;
}
